import ArticleLayout from '@/pages/article/components/ArticleLayout';
import type { ArticleData } from '@/pages/article/components/ArticleLayout';

const versandData: ArticleData = {
  slug: 'versand-guide',
  title: 'OOPBuy Versand nach Deutschland: Der komplette Guide 2026',
  subtitle: 'Versandkosten, Lieferzeiten und Zoll für deutsche Käufer',
  intro: 'Der <strong>Versand nach Deutschland</strong> ist einer der wichtigsten Aspekte beim Einkauf über OOPBuy. In diesem Guide erfährst du alles über Versandlinien, Kosten, Lieferzeiten, Zollbestimmungen und wie du dein Paket sicher und günstig nach Hause bekommst.',
  heroImgUrl: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20shipping%20and%20logistics%20concept%20with%20a%20neatly%20packaged%20box%20surrounded%20by%20fashion%20accessories%20on%20a%20warm%20beige%20desk%2C%20soft%20natural%20light%20streaming%20from%20window%2C%20clean%20editorial%20product%20photography%2C%20cream%20and%20taupe%20color%20palette%2C%20sophisticated%20composition%2C%20airy%20and%20organized%20atmosphere&width=1600&height=800&seq=versand-hero-v2-01&orientation=landscape',
  heroImgAlt: 'OOPBuy Versand nach Deutschland Guide',
  updatedAt: 'Juli 2026',
  readTime: '10 Minuten',
  tags: [
    { label: 'Versand', color: 'accent' },
    { label: 'Deutschland', color: 'primary' },
    { label: 'Guide 2026', color: 'secondary' },
  ],
  sections: [
    {
      id: 'section-1',
      title: 'Welche Versandoptionen bietet OOPBuy für Deutschland?',
      type: 'text',
      content: 'OOPBuy arbeitet mit mehreren internationalen Logistikpartnern zusammen, um Pakete sicher nach Deutschland zu liefern. Die Auswahl der richtigen <strong>Versandlinie</strong> hängt von deinem Budget, der gewünschten Liefergeschwindigkeit und dem Paketgewicht ab. Hier sind die wichtigsten Optionen im Detail.',
    },
    {
      id: 'section-2',
      title: 'Versandlinien im Vergleich',
      type: 'table',
      tableHeaders: ['Versandlinie', 'Lieferzeit', 'Kosten', 'Tracking', 'Empfehlung'],
      tableRows: [
        ['DHL Europe Line', '8-14 Tage', '€15-30', '★★★★★', 'Beste Wahl'],
        ['DHL Tarifless Line', '10-16 Tage', '€20-40', '★★★★★', 'Sehr gut'],
        ['EUB', '10-20 Tage', '€8-18', '★★★☆☆', 'Günstig'],
        ['EMS', '7-15 Tage', '€20-35', '★★★★☆', 'Schnell'],
        ['DPD Europe Line', '7-12 Tage', '€18-35', '★★★★☆', 'Zuverlässig'],
        ['GLS Europe', '8-14 Tage', '€15-30', '★★★★☆', 'Solide'],
        ['China Post Air Mail', '15-30 Tage', '€5-15', '★★☆☆☆', 'Sparoption'],
      ],
      note: 'Alle Preise sind Richtwerte für ein Paket mit ca. 2-3 kg. Die tatsächlichen Kosten variieren je nach Gewicht und Volumen.',
    },
    {
      id: 'section-3',
      title: 'DHL Europe Line – Die beste Wahl für Deutschland',
      type: 'text',
      content: 'Die <strong>DHL Europe Line</strong> ist für die meisten deutschen OOPBuy-Nutzer die optimale Versandoption. Sie bietet ein ausgewogenes Verhältnis aus Geschwindigkeit, Kosten und Sicherheit.<br/><br/>Vorteile der DHL Europe Line:<br/>• Zollabwicklung bereits im Preis enthalten (keine Überraschungen)<br/>• Lückenloses Tracking von China bis zur Haustür<br/>• Versicherung bis 500 € inklusive<br/>• Zustellung durch DHL in Deutschland (gewohnter Service)<br/>• Keine zusätzlichen Gebühren bei der Zustellung',
    },
    {
      id: 'section-4',
      title: 'Versandkosten sparen: Meine besten Tipps',
      type: 'tips',
      content: 'Mit diesen praktischen Strategien kannst du deine <strong>Versandkosten deutlich reduzieren</strong>:',
      tips: [
        { title: 'Mehrere Artikel bündeln', desc: 'Sammle mehrere Bestellungen im Warehouse und verschicke sie als ein Paket. Das spart pro Artikel oft 50-70% der Versandkosten.', icon: 'ri-stack-line' },
        { title: 'Verpackung optimieren', desc: 'Lass überflüssige Verpackungen wie Schuhkartons entfernen. Das reduziert Volumen und Gewicht – und damit die Kosten.', icon: 'ri-recycle-line' },
        { title: 'Gewichtsgrenzen beachten', desc: 'Jede Versandlinie hat optimale Gewichtsbereiche. Bleibe innerhalb dieser Grenzen für das beste Preis-Leistungs-Verhältnis.', icon: 'ri-scales-line' },
        { title: 'Langsamere Linien wählen', desc: 'Wenn du es nicht eilig hast, wähle EUB oder China Post Air Mail. Die Einsparung kann 50% oder mehr betragen.', icon: 'ri-timer-line' },
        { title: 'Aktionszeiten nutzen', desc: 'OOPBuy bietet regelmäßig Versandgutscheine an. Halte Ausschau nach Aktionen, besonders um Feiertage herum.', icon: 'ri-coupon-line' },
      ],
    },
    {
      id: 'section-5',
      title: 'Zoll und Einfuhrabgaben für Deutschland',
      type: 'text',
      content: 'Der deutsche Zoll ist für viele OOPBuy-Neulinge ein Buch mit sieben Siegeln. Dabei ist es gar nicht so kompliziert, wenn man die Grundregeln kennt:<br/><br/><strong>Wichtige Zoll-Fakten:</strong><br/>• Warenwert unter 150 €: Es fällt nur die Einfuhrumsatzsteuer an (19% bzw. 7% ermäßigt)<br/>• Warenwert über 150 €: Zusätzlich können Zollgebühren anfallen (je nach Produktkategorie 0-17%)<br/>• Geschenke bis 45 € sind zollfrei<br/>• DHL Tarifless Line und DHL Europe Line wickeln den Zoll für dich ab<br/>• Bei anderen Linien musst du das Paket eventuell selbst beim Zoll abholen',
    },
    {
      id: 'section-6',
      title: 'So deklarierst du dein Paket richtig',
      type: 'steps',
      content: 'Die korrekte <strong>Deklaration</strong> ist entscheidend für eine reibungslose Zollabwicklung. So gehst du vor:',
      steps: [
        { step: '1.', title: 'Realistischen Wert angeben', desc: 'Deklariere den tatsächlichen Kaufpreis. Der Zoll vergleicht mit Marktpreisen – Unterdeklaration kann zu Strafen führen.' },
        { step: '2.', title: 'Produktkategorie korrekt wählen', desc: 'Wähle die passende Kategorie (z.B. Schuhe, Kleidung, Elektronik). Falsche Angaben verzögern die Abwicklung.' },
        { step: '3.', title: 'Menge pro Position angeben', desc: 'Gib für jeden Artikeltyp die genaue Stückzahl an, z.B. 2x T-Shirt, 1x Sneaker.' },
        { step: '4.', title: 'Rechnung beilegen lassen', desc: 'OOPBuy legt automatisch eine Commercial Invoice bei. Prüfe die Angaben vor dem Versand.' },
      ],
    },
    {
      id: 'section-7',
      title: 'Tracking und Paketverfolgung',
      type: 'text',
      content: 'Sobald dein Paket versendet wurde, erhältst du eine Tracking-Nummer. Mit dieser kannst du den Weg deines Pakets von China bis zu deiner Haustür verfolgen.<br/><br/>Empfohlene Tracking-Apps und Websites:<br/>• 17TRACK – Unterstützt fast alle chinesischen Logistiker<br/>• DHL Sendungsverfolgung – Sobald das Paket in Deutschland ist<br/>• Aftership – Gute Übersicht mit Push-Benachrichtigungen',
    },
    {
      id: 'section-8',
      title: 'Häufige Versandprobleme und Lösungen',
      type: 'faq',
      faqs: [
        { q: 'Mein Paket steckt seit Tagen beim Zoll fest. Was tun?', a: 'Das ist normal und kein Grund zur Sorge. Der Zoll braucht manchmal 3-7 Werktage. Warte ab – in 95% der Fälle wird das Paket ohne dein Zutun freigegeben. Sollte der Zoll dich kontaktieren, reiche die geforderten Unterlagen (Rechnung, Zahlungsnachweis) ein.' },
        { q: 'Die Tracking-Nummer funktioniert nicht.', a: 'Es kann 24-48 Stunden dauern, bis die Tracking-Nummer im System aktiv ist. Prüfe sie auch auf 17TRACK – manche Nummern werden dort früher erkannt als bei DHL.' },
        { q: 'Mein Paket ist beschädigt angekommen.', a: 'Dokumentiere den Schaden mit Fotos und kontaktiere sofort den OOPBuy-Support. Bei versicherten Sendungen (DHL, EMS) erstattet die Versicherung den Schaden in der Regel innerhalb von 2-4 Wochen.' },
        { q: 'Kann ich die Versandadresse nach dem Versand noch ändern?', a: 'Nein, nachdem das Paket versendet wurde, ist keine Adressänderung mehr möglich. Prüfe deine Adresse daher vor dem Versand genau.' },
        { q: 'Was passiert, wenn ich das Paket nicht annehme?', a: 'Das Paket geht an den Absender zurück. Du trägst die Rücksendekosten und eventuelle Lagergebühren. Nimm Pakete daher immer an oder lasse sie bei Nichtzustellung an eine Packstation umleiten.' },
      ],
    },
    {
      id: 'section-9',
      title: 'Fazit: Der optimale Versandweg für dein OOPBuy Paket',
      type: 'cta',
      content: 'Für die meisten deutschen OOPBuy-Nutzer ist die DHL Europe Line oder DHL Tarifless Line die beste Wahl. Du erhältst schnelle Lieferung, volles Tracking und eine stressfreie Zollabwicklung – alles zu einem fairen Preis.',
      ctaText: 'OOPBuy Spreadsheet entdecken',
      ctaLink: 'https://kaspreadsheet.com',
      ctaExternal: true,
      secondaryCtaText: 'Zurück zur Startseite',
      secondaryCtaLink: '/',
    },
  ],
  relatedLinks: [
    { label: 'OOPBuy Startseite', to: '/' },
    { label: 'OOPBuy Spreadsheet Guide', to: '/oopbuy-spreadsheet-guide' },
    { label: 'OOPBuy QC Guide', to: '/qc-guide' },
    { label: 'OOPBuy vs Pandabuy', to: '/oopbuy-vs-pandabuy' },
    { label: 'OOPBuy Produktkategorien', to: '/' },
  ],
};

export default function VersandGuide() {
  return <ArticleLayout data={versandData} />;
}